import { createPortal } from 'react-dom';
import { useEffect, useCallback } from 'react';
import { Plus, Check } from 'lucide-react';
import { ProfileAvatar } from '@/components/domain/shared/ProfileAvatar';
import { SubPageHeader } from '@/components/ui/SubPageHeader';
import { useRegisterOverlay } from '@/stores/overlayStore';

export interface HouseholdOption {
  id: string;
  name: string;
  emoji: string;
  emojiColor?: string;
  imagePath?: string | null;
  accessLevel: string;
  isCurrent: boolean;
}

export interface SwitchHouseholdModalProps {
  open: boolean;
  onClose: () => void;
  households: HouseholdOption[];
  onSelect: (householdId: string) => void;
  /**
   * Optional. When omitted, the "Create new household" CTA is hidden.
   * An account owns at most one household, so AppShell passes this only
   * to someone who owns none yet (YAR-503).
   */
  onCreateNew?: () => void;
}

export function SwitchHouseholdModal({
  open,
  onClose,
  households,
  onSelect,
  onCreateNew,
}: SwitchHouseholdModalProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  // Escape closes, and the page behind is frozen while this is up. The
  // scroll lock matters now that the surface is full-screen: html/body is
  // the page scroller, so without it a drag anywhere on the switcher
  // scrolls Settings underneath it. Same treatment as FullScreenSheet.
  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', handleKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, handleKeyDown]);

  // Full-screen while open: hide and pause the bottom toast slot (YAR-476).
  useRegisterOverlay(open);

  if (!open) return null;

  return createPortal(
    /*
     * Presented as a full-screen Settings sub-page, not a bottom sheet.
     *
     * It used to be a `flex-1` panel with `mt-3` inside a blurred backdrop,
     * which is a bottom sheet that happens to be viewport-tall: the 12px
     * margin was the only thing the backdrop ever showed, so it read as an
     * unexplained gap above an otherwise full-screen surface rather than as
     * a sheet (YAR-390). Kelvin's call was to match the sub-pages instead,
     * so the backdrop, the rounded top and the margin are all gone and the
     * nav is the shared `SubPageHeader`.
     */
    <div
      className="fixed inset-0 z-[202] flex flex-col bg-background"
      role="dialog"
      aria-modal="true"
      aria-label="Switch Household"
    >
      <SubPageHeader
        title="Switch Household"
        onBack={onClose}
        leading="close"
      />

      {/* Household list */}
      <div className="flex flex-col gap-2 px-5 pt-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] overflow-y-auto flex-1">
        {households.map((household) => (
          <button
            key={household.id}
            type="button"
            onClick={() => onSelect(household.id)}
            className={[
              'flex gap-2 items-center w-full rounded-xl border border-border px-4 py-3 text-left cursor-pointer',
              household.isCurrent ? 'bg-muted' : 'bg-background',
            ].join(' ')}
          >
            <ProfileAvatar
              imagePath={household.imagePath}
              emoji={household.emoji}
              color={household.emojiColor}
              size="lg"
            />
            <div className="flex flex-col gap-0.5 flex-1 min-w-0">
              <span className="text-base font-medium text-foreground truncate">
                {household.name}
              </span>
              <span className="text-sm text-muted-foreground">
                {household.accessLevel}
              </span>
            </div>
            {household.isCurrent && (
              <Check
                size={24}
                className="shrink-0 text-foreground"
              />
            )}
          </button>
        ))}

        {/* Create new household — only when wired up by the caller. */}
        {onCreateNew && (
          <button
            type="button"
            onClick={onCreateNew}
            className="border border-dashed border-border-strong rounded-xl flex items-center justify-center px-4 py-3 w-full cursor-pointer"
          >
            <span className="text-base font-semibold text-muted-foreground">
              Create new household
            </span>
            <Plus
              size={16}
              className="ml-1.5 text-muted-foreground"
            />
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
}
